import { useParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Clock, FileText, Phone, Globe, 
  ArrowRight, DollarSign, Users, Award, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Premium animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { staggerChildren: 0.1, delayChildren: 0.1 } 
  }
};

// Dynamic page template that fetches from CMS API or uses static fallback
const DynamicPage = ({ pageData: staticPageData }) => {
  const location = useLocation();
  const [pageData, setPageData] = useState(staticPageData);
  const [loading, setLoading] = useState(!staticPageData);
  const [cmsContent, setCmsContent] = useState(null);
  
  // Extract slug from URL path
  const getSlugFromPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1];
  };

  // Fetch content from CMS API
  useEffect(() => {
    const fetchCmsContent = async () => {
      const slug = getSlugFromPath();
      if (!slug) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/pages/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setCmsContent(data);
          // If CMS has content, merge with static data or use CMS data
          if (data.content) {
            setPageData(prev => ({
              ...prev,
              title: data.title || prev?.title,
              subtitle: data.meta_description || prev?.subtitle,
              category: data.category || prev?.category,
              metaTitle: data.meta_title,
              metaDescription: data.meta_description,
              cmsContent: data.content, // Store raw CMS markdown content
            }));
          }
        }
      } catch (error) {
        console.log('CMS fetch failed, using static content');
      } finally {
        setLoading(false);
      }
    };

    fetchCmsContent();
  }, [location.pathname]);

  // Update document title + meta description for SEO.
  // Priority: explicit metaTitle from CMS/pageContent > human-readable title.
  // Avoids double brand suffix when caller has already branded the title.
  useEffect(() => {
    const rawTitle =
      cmsContent?.meta_title ||
      pageData?.metaTitle ||
      cmsContent?.title ||
      pageData?.title;

    if (rawTitle) {
      const alreadyBranded =
        rawTitle.includes(' | ') ||
        rawTitle.toLowerCase().includes('zafco');
      document.title = alreadyBranded ? rawTitle : `${rawTitle} | Zafco HR`;
    }

    // Update meta description
    const metaDesc =
      cmsContent?.meta_description || pageData?.metaDescription;
    if (metaDesc) {
      let metaTag = document.querySelector('meta[name="description"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = 'description';
        document.head.appendChild(metaTag);
      }
      metaTag.content = metaDesc;
    }

    return () => {
      // Restore canonical homepage title when leaving CMS page
      document.title =
        'Best Overseas Recruitment Agency in Delhi | MEA Approved | Zafco HR';
    };
  }, [pageData?.title, pageData?.metaTitle, pageData?.metaDescription, cmsContent]);
  
  const openWhatsApp = () => {
    const message = `Hello Zafco, I need help with ${pageData?.title || 'overseas recruitment'}`;
    window.open(`https://wa.me/919958848998?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A028] mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!pageData && !cmsContent) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4 font-syne">Page Coming Soon</h1>
          <p className="text-slate-400 mb-6">This page is under development. Contact us for more information.</p>
          <Button onClick={openWhatsApp} className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold">Contact Us</Button>
        </div>
      </div>
    );
  }

  // Use CMS content if available, otherwise use static content
  const title = cmsContent?.title || pageData?.title;
  const subtitle = cmsContent?.meta_description || pageData?.subtitle;
  const category = cmsContent?.category || pageData?.category;
  const heroImage = pageData?.heroImage;
  const content = pageData?.content || {};
  const { intro, sections, eligibility, benefits, documents, processSteps } = content;
  const hasCmsContent = cmsContent?.content && cmsContent.content.trim().length > 0;

  // Generate breadcrumbs from URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    label: part.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    path: index < pathParts.length - 1 ? '/' + pathParts.slice(0, index + 1).join('/') : undefined
  }));

  return (
    <div className="bg-slate-950" data-testid="dynamic-page">
      <PageHeader 
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        backgroundImage={heroImage}
      />

      {/* CMS Content Section - Rendered if CMS has content */}
      {hasCmsContent && (
        <section className="py-20 bg-slate-950">
          <div className="container max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-3 gap-16">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="lg:col-span-2"
              >
                <div className="prose prose-invert prose-lg max-w-none 
                  prose-headings:font-syne prose-headings:text-white prose-headings:font-bold
                  prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-6 prose-h1:mt-8
                  prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mb-4 prose-h2:mt-8 prose-h2:text-[#C5A028]
                  prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6
                  prose-p:text-slate-400 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:text-slate-400 prose-ul:my-4
                  prose-ol:text-slate-400 prose-ol:my-4
                  prose-li:mb-2
                  prose-strong:text-white
                  prose-a:text-[#C5A028] prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-[#C5A028] prose-blockquote:bg-slate-900/50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                  prose-code:text-[#C5A028] prose-code:bg-slate-900/50 prose-code:px-2 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-slate-900/80 prose-pre:border prose-pre:border-white/10
                ">
                  <ReactMarkdown>{cmsContent.content}</ReactMarkdown>
                </div>

                {/* Quick Stats */}
                <motion.div 
                  variants={fadeInUp}
                  className="grid grid-cols-2 gap-4 mt-12"
                >
                  <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                    <Clock className="w-6 h-6 text-[#C5A028] mb-2" />
                    <p className="text-2xl font-bold text-white font-syne">6-18 Mo</p>
                    <p className="text-sm text-slate-400">Processing Time</p>
                  </div>
                  <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                    <Award className="w-6 h-6 text-[#C5A028] mb-2" />
                    <p className="text-2xl font-bold text-white font-syne">98%</p>
                    <p className="text-sm text-slate-400">Success Rate</p>
                  </div>
                </motion.div>

                <motion.div variants={fadeInUp} className="mt-8">
                  <Button 
                    onClick={openWhatsApp}
                    className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-8 py-6 rounded-full"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Get Free Assessment
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInRight}
                className="glass-premium rounded-2xl p-8 sticky top-24 h-fit"
              >
                <h3 className="text-xl font-bold text-white font-syne mb-6">
                  Free Consultation
                </h3>
                <ConsultationForm variant="dark" />
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Legacy Content Sections - Rendered if no CMS content */}
      {!hasCmsContent && (
        <>
          {/* Introduction Section */}
          <section className="py-20 bg-slate-950">
            <div className="container max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-16 items-start">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                >
                  <motion.span 
                    variants={fadeInUp}
                    className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase"
                  >
                    {category} Immigration
                  </motion.span>
                  <motion.h2 
                    variants={fadeInUp}
                    className="text-3xl md:text-4xl font-bold text-white font-syne mt-2 mb-6"
                  >
                    Expert Recruitment Assistance
                  </motion.h2>
                  
                  {intro && (
                    <motion.p 
                      variants={fadeInUp}
                      className="text-slate-400 mb-8 leading-relaxed text-lg"
                    >
                      {intro}
                    </motion.p>
                  )}

                  {/* Sections */}
                  {sections && sections.map((section) => (
                    <motion.div 
                      key={section.title} 
                      variants={fadeInUp}
                      className="mb-6"
                    >
                      <h3 className="text-xl font-bold text-white mb-3 font-syne">{section.title}</h3>
                      <p className="text-slate-400 leading-relaxed">{section.content}</p>
                    </motion.div>
                  ))}

                  {/* Quick Stats */}
                  <motion.div 
                    variants={fadeInUp}
                    className="grid grid-cols-2 gap-4 my-8"
                  >
                    <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                      <Clock className="w-6 h-6 text-[#C5A028] mb-2" />
                      <p className="text-2xl font-bold text-white font-syne">6-18 Mo</p>
                      <p className="text-sm text-slate-400">Processing Time</p>
                    </div>
                    <div className="bg-slate-900/50 border border-white/5 rounded-xl p-4">
                      <Award className="w-6 h-6 text-[#C5A028] mb-2" />
                      <p className="text-2xl font-bold text-white font-syne">98%</p>
                      <p className="text-sm text-slate-400">Success Rate</p>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <Button 
                      onClick={openWhatsApp}
                      className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-8 py-6 rounded-full"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Get Free Assessment
                    </Button>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInRight}
                  className="glass-premium rounded-2xl p-8 sticky top-24"
                >
                  <h3 className="text-xl font-bold text-white font-syne mb-6">
                    Free Consultation
                  </h3>
                  <ConsultationForm variant="dark" />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          {benefits && benefits.length > 0 && (
            <section className="py-20 bg-slate-900/30">
              <div className="container max-w-7xl mx-auto px-6 md:px-12">
                <motion.div 
                  className="text-center mb-16"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <p className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase mb-4">Advantages</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white font-syne mb-4">
                    Key Benefits
                  </h2>
                </motion.div>

                <motion.div 
                  className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                >
                  {benefits.map((benefit) => (
                    <motion.div
                      key={benefit}
                      className="bg-slate-900/50 border border-white/5 rounded-xl p-6 hover:border-[#C5A028]/30 transition-colors"
                      variants={fadeInUp}
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                    >
                      <CheckCircle className="w-8 h-8 text-[#C5A028] mb-3" />
                      <p className="text-white font-medium">{benefit}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>
          )}

          {/* Eligibility & Documents */}
          {(eligibility || documents) && (
            <section className="py-20 bg-slate-950">
              <div className="container max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-2 gap-16">
                  {eligibility && (
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerContainer}
                    >
                      <motion.p variants={fadeInUp} className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase mb-4">Requirements</motion.p>
                      <motion.h2 variants={fadeInUp} className="text-2xl md:text-3xl font-bold text-white font-syne mb-8">
                        Eligibility Criteria
                      </motion.h2>
                      <div className="space-y-4">
                        {eligibility.map((item, index) => (
                          <motion.div 
                            key={index} 
                            variants={fadeInUp}
                            className="bg-slate-900/50 border border-white/5 rounded-xl p-5 flex items-start gap-4 hover:border-[#C5A028]/20 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-lg bg-[#C5A028]/10 flex items-center justify-center flex-shrink-0">
                              <CheckCircle className="w-5 h-5 text-[#C5A028]" />
                            </div>
                            <p className="text-slate-300">{item}</p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {documents && (
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerContainer}
                    >
                      <motion.p variants={fadeInUp} className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase mb-4">Documentation</motion.p>
                      <motion.h2 variants={fadeInUp} className="text-2xl md:text-3xl font-bold text-white font-syne mb-8">
                        Required Documents
                      </motion.h2>
                      <motion.div 
                        variants={fadeInUp}
                        className="bg-slate-900/50 border border-white/5 rounded-2xl p-8"
                      >
                        <ul className="space-y-4">
                          {documents.map((doc) => (
                            <li key={doc} className="flex items-center gap-3 text-slate-300">
                              <FileText className="w-5 h-5 text-[#C5A028]" />
                              {doc}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Process Steps */}
          {processSteps && processSteps.length > 0 && (
            <section className="py-20 bg-slate-900/30">
              <div className="container max-w-7xl mx-auto px-6 md:px-12">
                <motion.div 
                  className="text-center mb-16"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <p className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase mb-4">Process</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white font-syne mb-4">
                    Step-by-Step Process
                  </h2>
                </motion.div>

                <div className="max-w-4xl mx-auto">
                  {processSteps.map((step, index) => (
                    <motion.div
                      key={step.title || `step-${index}`}
                      className="flex gap-6 mb-8 last:mb-0"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeInUp}
                    >
                      <div className="flex flex-col items-center">
                        <motion.div 
                          className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C5A028] to-[#EAB308] flex items-center justify-center text-black font-bold font-syne"
                          whileHover={{ scale: 1.1 }}
                        >
                          {step.step}
                        </motion.div>
                        {index < processSteps.length - 1 && (
                          <div className="w-0.5 flex-1 bg-gradient-to-b from-[#C5A028] to-[#C5A028]/20 mt-2" />
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <div className="bg-slate-900/50 border border-white/5 rounded-xl p-6 hover:border-[#C5A028]/20 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-bold text-white font-syne">{step.title}</h3>
                            <span className="text-sm text-[#C5A028] font-medium">{step.duration}</span>
                          </div>
                          <p className="text-slate-400">{step.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA Section - Always shown */}
      <section className="py-20 bg-slate-950 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-syne mb-6"
            >
              Ready to Start Your Journey?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto"
            >
              Get your free assessment and personalized guidance from Zafco's immigration experts today.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button 
                onClick={openWhatsApp}
                size="lg"
                className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-10 py-7 text-lg rounded-full btn-gold-glow"
              >
                <Phone className="w-6 h-6 mr-3" />
                Get Free Assessment
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DynamicPage;
